import React, { useEffect, useRef } from 'react';
import { ADSENSE_CLIENT_ID, ADSENSE_SLOTS } from '../config/version';

export type AdSlotType = 'top-banner' | 'in-content' | 'sidebar' | 'between-posts' | 'footer';

interface AdSlotProps {
  slotType: AdSlotType;
  className?: string;
  adSlotId?: string; // Optional specific slot ID override
}

export const AdSlot: React.FC<AdSlotProps> = ({
  slotType,
  className = '',
  adSlotId,
}) => {
  const adsenseClient = (import.meta as any)?.env?.VITE_ADSENSE_CLIENT || ADSENSE_CLIENT_ID;
  const isAdSenseConfigured = Boolean(adsenseClient && adsenseClient.startsWith('ca-pub-'));
  const insRef = useRef<HTMLModElement>(null);
  const pushedRef = useRef(false);

  // Determine specific slot ID and format based on slotType
  const resolvedSlotId = adSlotId || (
    slotType === 'in-content'
      ? ADSENSE_SLOTS.IN_ARTICLE
      : slotType === 'footer'
        ? ADSENSE_SLOTS.MULTIPLEX
        : ADSENSE_SLOTS.DISPLAY_AUTO
  );

  useEffect(() => {
    if (!isAdSenseConfigured || typeof window === 'undefined' || pushedRef.current) return;

    try {
      if (insRef.current && !insRef.current.getAttribute('data-adsbygoogle-status')) {
        // @ts-ignore
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        pushedRef.current = true;
      }
    } catch {
      // Safe fallback if blocked by browser extension or privacy sandbox
    }
  }, [isAdSenseConfigured]);

  // Dimension classes to reserve layout space and prevent Cumulative Layout Shift (CLS)
  const getContainerDimensions = () => {
    switch (slotType) {
      case 'top-banner':
        return 'min-h-[100px] w-full max-w-5xl mx-auto my-6';
      case 'sidebar':
        return 'min-h-[280px] w-full max-w-[320px] mx-auto my-6';
      case 'in-content':
        return 'min-h-[160px] sm:min-h-[250px] w-full max-w-3xl mx-auto my-8';
      case 'between-posts':
        return 'min-h-[110px] w-full max-w-4xl mx-auto my-8';
      case 'footer':
        return 'min-h-[220px] w-full max-w-5xl mx-auto my-8';
      default:
        return 'min-h-[100px] w-full';
    }
  };

  // If AdSense client ID is not configured, return an invisible zero-CLS wrapper
  if (!isAdSenseConfigured) {
    return (
      <div
        className={`ad-container-dormant flex items-center justify-center pointer-events-none opacity-0 select-none ${getContainerDimensions()} ${className}`}
        aria-hidden="true"
      />
    );
  }

  // Render format specific to unit type
  return (
    <div
      className={`ad-slot-wrapper rounded-2xl overflow-hidden border border-border/40 bg-surface-card/60 p-3 text-center transition-colors ${getContainerDimensions()} ${className}`}
      aria-label="Advertisement"
    >
      <div className="flex items-center justify-between px-2 mb-2 border-b border-border/20 pb-1">
        <span className="text-[10px] font-mono tracking-widest uppercase text-graphite-muted">
          Advertisement
        </span>
        <span className="text-[9px] font-mono tracking-wider uppercase text-graphite-muted/60">
          Google AdSense · Official Partner
        </span>
      </div>

      {slotType === 'in-content' ? (
        /* In-Article Fluid Ad Unit */
        <ins
          ref={insRef}
          className="adsbygoogle"
          style={{ display: 'block', textAlign: 'center' }}
          data-ad-layout="in-article"
          data-ad-format="fluid"
          data-ad-client={adsenseClient}
          data-ad-slot={resolvedSlotId}
        />
      ) : slotType === 'footer' ? (
        /* Multiplex Recommended Ad Unit */
        <ins
          ref={insRef}
          className="adsbygoogle"
          style={{ display: 'block' }}
          data-ad-format="autorelaxed"
          data-ad-client={adsenseClient}
          data-ad-slot={resolvedSlotId}
        />
      ) : (
        /* Display Auto Responsive Banner */
        <ins
          ref={insRef}
          className="adsbygoogle"
          style={{ display: 'block' }}
          data-ad-client={adsenseClient}
          data-ad-slot={resolvedSlotId}
          data-ad-format="auto"
          data-full-width-responsive="true"
        />
      )}
    </div>
  );
};

export default AdSlot;
